'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { HeroBanner, type HeroBannerPageTitle } from '@/components/frontend/common/hero-banner';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import Image from 'next/image';

type Testimonial = {
    id: string;
    name: string;
    role?: string | null;
    content: string;
    image?: string | null;
};

// Fallback images - use empty string to show placeholder instead of 404
const fallbackImages = [
    '', // No fallback - will show placeholder
    '', // No fallback - will show placeholder
    '', // No fallback - will show placeholder
];

export function TestimonialsPageContent({ initialTestimonials, initialPageTitle }: { initialTestimonials?: Testimonial[]; initialPageTitle?: HeroBannerPageTitle | null } = {}) {
    const t = useTranslations('homeTestimonials');
    const tTestimonials = useTranslations('testimonials');
    const locale = useLocale();
    const [testimonials, setTestimonials] = React.useState<Testimonial[]>(initialTestimonials ?? []);
    const [isLoading, setIsLoading] = React.useState(!initialTestimonials?.length);

    React.useEffect(() => {
        if (initialTestimonials?.length) {
            setTestimonials(initialTestimonials);
            setIsLoading(false);
            return;
        }
        const fetchTestimonials = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`/api/testimonials?locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });
                if (response.ok) {
                    const data = await response.json();
                    setTestimonials(Array.isArray(data) ? data : []);
                } else {
                    setTestimonials([]);
                }
            } catch (error) {
                setTestimonials([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTestimonials();
    }, [locale, initialTestimonials]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
            <HeroBanner
                page="testimonials"
                title={t('title')}
                description={t('description')}
                pill={t('pillLabel')}
                initialPageTitle={initialPageTitle}
            />

            <SectionWrapper variant="default" padding="lg" className="relative overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40">
                    <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                    <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
                </div>

                <div className="relative z-10 container">
                    {isLoading ? (
                        <div className="text-center py-12">
                            <Typography variant="p" className="text-gray-500 dark:text-gray-400">
                                {tTestimonials('loading')}
                            </Typography>
                        </div>
                    ) : testimonials.length === 0 ? (
                        <div className="text-center py-12">
                            <Typography variant="p" className="text-gray-500 dark:text-gray-400">
                                {tTestimonials('noTestimonialsAvailable')}
                            </Typography>
                            <Typography variant="p" className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                                {tTestimonials('testimonialsWillAppear')}
                            </Typography>
                        </div>
                    ) : (
                        <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                            {testimonials.map((testimonial) => {
                                const imageUrl = testimonial.image || null; // Don't use fallback images that don't exist
                                return (
                                    <div key={testimonial.id} className="mb-6 break-inside-avoid">
                                        <Card
                                            title=""
                                            description=""
                                            showFooterLink={false}
                                            className={cn(
                                                'rounded-3xl border border-gray-100 dark:border-gray-800',
                                                'bg-white dark:bg-[#050816]',
                                                'shadow-[0_18px_40px_rgba(15,23,42,0.08)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.65)]',
                                                'p-0'
                                            )}
                                            renderContent={
                                                <div className="flex flex-col gap-4">
                                                    {/* Header: avatar + name / role */}
                                                    <header className="flex items-center gap-4">
                                                        <div className="relative h-12 w-12 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                                                            {imageUrl && imageUrl.startsWith('/uploads/') ? (
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={testimonial.name}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        // Prevent infinite retries - just hide the image
                                                                        if (!target.dataset.errorHandled) {
                                                                            target.dataset.errorHandled = 'true';
                                                                            target.style.display = 'none';
                                                                        }
                                                                    }}
                                                                />
                                                            ) : imageUrl && imageUrl.startsWith('http') ? (
                                                                <Image
                                                                    src={imageUrl}
                                                                    alt={testimonial.name}
                                                                    fill
                                                                    className="object-cover"
                                                                    onError={(e) => {
                                                                        const target = e.target as HTMLImageElement;
                                                                        // Prevent infinite retries
                                                                        if (!target.dataset.errorHandled) {
                                                                            target.dataset.errorHandled = 'true';
                                                                            target.style.display = 'none';
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-400">
                                                                    No Image
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <Typography
                                                                variant="p"
                                                                className="font-semibold text-gray-900 dark:text-white"
                                                            >
                                                                {testimonial.name}
                                                            </Typography>
                                                            {testimonial.role && (
                                                                <Typography
                                                                    variant="p"
                                                                    className="text-sm text-gray-500 dark:text-gray-400"
                                                                >
                                                                    {testimonial.role}
                                                                </Typography>
                                                            )}
                                                        </div>
                                                    </header>

                                                    {/* Divider */}
                                                    <div className="h-px bg-gray-100 dark:bg-gray-800 my-4" />

                                                    {/* Body */}
                                                    <Typography
                                                        variant="p"
                                                        className="text-gray-700 dark:text-gray-200 leading-relaxed"
                                                    >
                                                        {testimonial.content}
                                                    </Typography>
                                                </div>
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SectionWrapper>
        </div>
    );
}

